import { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getPricingPlans, createPricingPlan, updatePricingPlan } from '../../lib/api'
import { pricingPlanSchema } from '../../lib/schemas'
import { Input } from '../../components/ui/Input'
import { Toggle } from '../../components/ui/Toggle'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { RichTextEditor } from '../../components/ui/RichTextEditor'

export default function PricingForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: plans } = useQuery({ queryKey: ['pricing'], queryFn: getPricingPlans, enabled: isEdit })
  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    resolver: zodResolver(pricingPlanSchema),
    defaultValues: { name: '', price: '', period: '', badge: '', isHighlighted: false, description: '', ctaText: '', ctaHref: '', order: 0, isActive: true },
  })

  useEffect(() => {
    if (isEdit && plans) {
      const item = plans.find(p => p.id === id)
      if (item) reset(item)
    }
  }, [plans, id, isEdit, reset])

  const mutation = useMutation({
    mutationFn: (data) => isEdit ? updatePricingPlan(id, data) : createPricingPlan(data),
    onSuccess: (result) => {
      qc.setQueryData(['pricing'], old =>
        isEdit
          ? (old ?? []).map(p => p.id === id ? result : p)
          : [...(old ?? []), result]
      )
      toast.success(isEdit ? 'Plan updated' : 'Plan created')
      navigate('/pricing')
    },
    onError: (e) => toast.error(e.message),
  })

  if (isEdit && !plans) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>

  return (
    <div className="">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/pricing"><Button variant="ghost" size="sm"><i className="ti ti-arrow-left" /></Button></Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Plan' : 'New Pricing Plan'}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Subscription pricing details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-6">
        <section className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Plan Details</h2>
          <Input label="Plan name" placeholder="Professional" error={errors.name?.message} {...register('name')} />
          <div className="grid grid-cols-3 gap-3">
            <Input label="Price" placeholder="$49" error={errors.price?.message} {...register('price')} />
            <Input label="Period" placeholder="month" error={errors.period?.message} {...register('period')} />
            <Input label="Badge" placeholder="Most Popular" error={errors.badge?.message} {...register('badge')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="CTA button text" placeholder="Get Started" error={errors.ctaText?.message} {...register('ctaText')} />
            <Input label="CTA link" placeholder="/contact" error={errors.ctaHref?.message} {...register('ctaHref')} />
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Description</h2>
          <Controller name="description" control={control} render={({ field }) => (
            <RichTextEditor placeholder="Describe what's included in this plan..." value={field.value} onChange={field.onChange} error={errors.description?.message} />
          )} />
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Settings</h2>
          <Input label="Sort order" type="number" placeholder="0" error={errors.order?.message} {...register('order')} />
          <Controller name="isHighlighted" control={control} render={({ field }) => (
            <Toggle label="Featured plan" description="Highlight this plan visually" checked={field.value} onChange={field.onChange} />
          )} />
          <Controller name="isActive" control={control} render={({ field }) => (
            <Toggle label="Active" description="Show on the public site" checked={field.value} onChange={field.onChange} />
          )} />
        </section>

        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
            {isEdit ? 'Update Plan' : 'Create Plan'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/pricing')}>Cancel</Button>
        </div>
      </form>
    </div>
  )
}
